import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import {
    DiagnosticEntry,
    DiagnosticMemorySnapshot,
    DiagnosticMetric,
    DiagnosticRecordInput,
    DiagnosticMetricUnit,
    DiagnosticSupportDataThreshold,
    DiagnosticSupportDataThresholdInput,
    DiagnosticScope,
    DiagnosticSummaryItem,
} from './diagnostic-types';

const MAX_ENTRIES = 1000;
const DEFAULT_GROUP = 'application';
const DECIMAL_PLACES = 2;
const DEFAULT_SUPPORT_DATA_AVERAGE_WARNING_MS = 500;
const DEFAULT_SUPPORT_DATA_MAX_WARNING_MS = 1500;
const DEFAULT_SUPPORT_DATA_NETWORK_WARNING_MS = 1200;
const DEFAULT_SUPPORT_DATA_CACHE_WARNING_MS = 300;

const DEFAULT_SUPPORT_DATA_THRESHOLDS: readonly DiagnosticSupportDataThreshold[] = [
    {
        id: 'support-data-default',
        maxAverageMs: DEFAULT_SUPPORT_DATA_AVERAGE_WARNING_MS,
        maxMs: DEFAULT_SUPPORT_DATA_MAX_WARNING_MS,
        maxAverageNetworkMs: DEFAULT_SUPPORT_DATA_NETWORK_WARNING_MS,
        maxCacheMs: DEFAULT_SUPPORT_DATA_CACHE_WARNING_MS,
    },
    {
        id: 'parcelas-fincas',
        formName: 'parcelas',
        sourceKey: 'fincas',
        maxCacheMs: DEFAULT_SUPPORT_DATA_CACHE_WARNING_MS,
        maxAverageNetworkMs: DEFAULT_SUPPORT_DATA_NETWORK_WARNING_MS,
    },
];

const NOOP_DIAGNOSTIC_SCOPE: DiagnosticScope = {
    addMetric: (): void => {},
    end: (): void => {},
    fail: (): void => {},
};

interface InternalDiagnosticScopeState {
    readonly id: number;
    readonly name: string;
    readonly group: string;
    readonly startedAt: number;
    readonly metadata?: Record<string, unknown>;
    readonly memoryStart?: DiagnosticMemorySnapshot;
    readonly metrics: DiagnosticMetric[];
    closed: boolean;
}

@Injectable({ providedIn: 'root' })
export class BrowserDiagnosticsService {
    private readonly enabled = environment.production !== true
        && environment.features?.enableDiagnostics === true;

    private readonly entries: DiagnosticEntry[] = [];

    private supportDataThresholds: DiagnosticSupportDataThreshold[] = [...DEFAULT_SUPPORT_DATA_THRESHOLDS];

    private nextId = 1;

    public constructor() {
        if (this.enabled && typeof window !== 'undefined') {
            window.__fitogestDiagnostics = {
                getEntries: () => this.getEntries(),
                summary: () => this.summary(),
                printSummary: () => this.printSummary(),
                clear: () => this.clear(),
                getSupportDataThresholds: () => this.getSupportDataThresholds(),
                setSupportDataThresholds: (thresholds: readonly DiagnosticSupportDataThresholdInput[]): void => {
                    this.setSupportDataThresholds(thresholds);
                },
                registerSupportDataThresholds: (thresholds: readonly DiagnosticSupportDataThresholdInput[]): void => {
                    this.registerSupportDataThresholds(thresholds);
                },
                resetSupportDataThresholds: () => this.resetSupportDataThresholds(),
            };
        }
    }

    public isEnabled(): boolean {
        return this.enabled;
    }

    public start(
        name: string,
        group: string = DEFAULT_GROUP,
        metadata?: Record<string, unknown>,
    ): DiagnosticScope {
        if (!this.enabled) {
            return NOOP_DIAGNOSTIC_SCOPE;
        }

        const memoryStart = this.readMemory();
        const state: InternalDiagnosticScopeState = {
            id: this.nextId,
            name,
            group,
            startedAt: this.now(),
            ...(metadata !== undefined ? { metadata } : {}),
            ...(memoryStart !== undefined ? { memoryStart } : {}),
            metrics: [],
            closed: false,
        };

        this.nextId += 1;

        return {
            addMetric: (metricName: string, value: number, unit: DiagnosticMetricUnit = 'custom'): void => {
                if (!this.enabled || state.closed || !Number.isFinite(value)) {
                    return;
                }

                state.metrics.push({ name: metricName, value, unit });
            },
            end: (endMetadata?: Record<string, unknown>): void => {
                this.closeScope(state, endMetadata);
            },
            fail: (error: unknown, endMetadata?: Record<string, unknown>): void => {
                this.closeScope(state, endMetadata, this.stringifyError(error));
            },
        };
    }

    public record(input: DiagnosticRecordInput): void {
        if (!this.enabled || !Number.isFinite(input.durationMs)) {
            return;
        }

        const durationMs = Math.max(0, this.round(input.durationMs));
        const endedAt = this.now();
        const memoryEnd = this.readMemory();
        const entry: DiagnosticEntry = {
            id: this.nextId,
            name: input.name,
            group: input.group ?? DEFAULT_GROUP,
            startedAt: endedAt - durationMs,
            endedAt,
            durationMs,
            ...(input.metadata !== undefined ? { metadata: input.metadata } : {}),
            metrics: [...(input.metrics ?? [])],
            ...(memoryEnd !== undefined ? { memoryEnd } : {}),
            ...(input.error !== undefined ? { error: this.stringifyError(input.error) } : {}),
        };

        this.nextId += 1;
        this.pushEntry(entry);
    }

    public getEntries(): DiagnosticEntry[] {
        return this.entries.map((entry) => ({
            ...entry,
            metrics: [...entry.metrics],
        }));
    }

    public clear(): void {
        this.entries.length = 0;
    }

    public summary(): DiagnosticSummaryItem[] {
        const grouped = new Map<string, DiagnosticSummaryItem>();

        for (const entry of this.entries) {
            const key = `${entry.group}::${entry.name}`;
            const current = grouped.get(key);

            if (!current) {
                grouped.set(key, {
                    name: entry.name,
                    group: entry.group,
                    calls: 1,
                    totalMs: this.round(entry.durationMs),
                    averageMs: this.round(entry.durationMs),
                    maxMs: this.round(entry.durationMs),
                    errors: entry.error ? 1 : 0,
                });
                continue;
            }

            const calls = current.calls + 1;
            const totalMs = current.totalMs + entry.durationMs;

            grouped.set(key, {
                name: current.name,
                group: current.group,
                calls,
                totalMs: this.round(totalMs),
                averageMs: this.round(totalMs / calls),
                maxMs: this.round(Math.max(current.maxMs, entry.durationMs)),
                errors: current.errors + (entry.error ? 1 : 0),
            });
        }

        return [...grouped.values()].sort((a, b) => b.totalMs - a.totalMs);
    }

    public getSupportDataThresholds(): DiagnosticSupportDataThreshold[] {
        return this.supportDataThresholds.map((threshold) => ({ ...threshold }));
    }

    public setSupportDataThresholds(thresholds: readonly DiagnosticSupportDataThresholdInput[]): void {
        this.supportDataThresholds = thresholds
            .map((threshold, index) => this.normalizeSupportDataThreshold(threshold, index))
            .filter((threshold): threshold is DiagnosticSupportDataThreshold => threshold !== null);
    }

    public registerSupportDataThresholds(thresholds: readonly DiagnosticSupportDataThresholdInput[]): void {
        const normalizedThresholds = thresholds
            .map((threshold, index) => this.normalizeSupportDataThreshold(threshold, index))
            .filter((threshold): threshold is DiagnosticSupportDataThreshold => threshold !== null);

        if (normalizedThresholds.length === 0) {
            return;
        }

        const thresholdsById = new Map(this.supportDataThresholds.map((threshold) => [threshold.id, threshold]));

        for (const threshold of normalizedThresholds) {
            thresholdsById.set(threshold.id, threshold);
        }

        this.supportDataThresholds = [...thresholdsById.values()];
    }

    public resetSupportDataThresholds(): void {
        this.supportDataThresholds = [...DEFAULT_SUPPORT_DATA_THRESHOLDS];
    }

    public printSummary(): void {
        if (!this.enabled) {
            console.warn('[Fitogest diagnostics] Disabled. Set environment.features.enableDiagnostics = true in a non-production build.');
            return;
        }

        console.log(this.summary());
    }

    private closeScope(
        state: InternalDiagnosticScopeState,
        metadata?: Record<string, unknown>,
        error?: string,
    ): void {
        if (!this.enabled || state.closed) {
            return;
        }

        state.closed = true;

        const endedAt = this.now();
        const mergedMetadata = {
            ...(state.metadata ?? {}),
            ...(metadata ?? {}),
        };
        const memoryEnd = this.readMemory();

        const entry: DiagnosticEntry = {
            id: state.id,
            name: state.name,
            group: state.group,
            startedAt: state.startedAt,
            endedAt,
            durationMs: this.round(endedAt - state.startedAt),
            ...(Object.keys(mergedMetadata).length > 0 ? { metadata: mergedMetadata } : {}),
            metrics: [...state.metrics],
            ...(state.memoryStart !== undefined ? { memoryStart: state.memoryStart } : {}),
            ...(memoryEnd !== undefined ? { memoryEnd } : {}),
            ...(error !== undefined ? { error } : {}),
        };

        this.pushEntry(entry);
    }

    private pushEntry(entry: DiagnosticEntry): void {
        this.entries.push(entry);

        if (this.entries.length > MAX_ENTRIES) {
            this.entries.splice(0, this.entries.length - MAX_ENTRIES);
        }
    }

    private now(): number {
        return typeof performance !== 'undefined' && typeof performance.now === 'function'
            ? performance.now()
            : Date.now();
    }

    private readMemory(): DiagnosticMemorySnapshot | undefined {
        if (typeof performance === 'undefined' || !performance.memory) {
            return undefined;
        }

        const memory = performance.memory;
        const snapshot: DiagnosticMemorySnapshot = {
            ...(typeof memory.usedJsHeapSize === 'number' ? { usedJsHeapSize: memory.usedJsHeapSize } : {}),
            ...(typeof memory.totalJsHeapSize === 'number' ? { totalJsHeapSize: memory.totalJsHeapSize } : {}),
            ...(typeof memory.jsHeapSizeLimit === 'number' ? { jsHeapSizeLimit: memory.jsHeapSizeLimit } : {}),
        };

        return Object.keys(snapshot).length > 0 ? snapshot : undefined;
    }

    private normalizeSupportDataThreshold(
        threshold: DiagnosticSupportDataThresholdInput,
        index: number,
    ): DiagnosticSupportDataThreshold | null {
        const formName = this.cleanText(threshold.formName);
        const sourceKey = this.cleanText(threshold.sourceKey);
        const phase = this.cleanText(threshold.phase);
        const maxAverageMs = this.positiveNumber(threshold.maxAverageMs);
        const maxMs = this.positiveNumber(threshold.maxMs);
        const maxAverageNetworkMs = this.positiveNumber(threshold.maxAverageNetworkMs);
        const maxCacheMs = this.positiveNumber(threshold.maxCacheMs);
        const hasAnyLimit = maxAverageMs !== null || maxMs !== null || maxAverageNetworkMs !== null || maxCacheMs !== null;

        if (!hasAnyLimit) {
            return null;
        }

        return {
            id: this.createSupportDataThresholdId(formName, sourceKey, phase, index),
            ...(formName !== null ? { formName } : {}),
            ...(sourceKey !== null ? { sourceKey } : {}),
            ...(phase !== null ? { phase } : {}),
            ...(maxAverageMs !== null ? { maxAverageMs } : {}),
            ...(maxMs !== null ? { maxMs } : {}),
            ...(maxAverageNetworkMs !== null ? { maxAverageNetworkMs } : {}),
            ...(maxCacheMs !== null ? { maxCacheMs } : {}),
        };
    }

    private createSupportDataThresholdId(
        formName: string | null,
        sourceKey: string | null,
        phase: string | null,
        index: number,
    ): string {
        return [
            formName ?? 'all-forms',
            sourceKey ?? 'all-sources',
            phase ?? 'all-phases',
            String(index + 1),
        ].join(':');
    }

    private cleanText(value: string | undefined): string | null {
        const trimmed = value?.trim();

        return trimmed ? trimmed : null;
    }

    private positiveNumber(value: number | undefined): number | null {
        return typeof value === 'number' && Number.isFinite(value) && value > 0 ? this.round(value) : null;
    }

    private stringifyError(error: unknown): string {
        if (error instanceof Error) {
            return error.message;
        }

        return String(error ?? 'Unknown error');
    }

    private round(value: number): number {
        const factor = 10 ** DECIMAL_PLACES;

        return Math.round(value * factor) / factor;
    }
}
